import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, SelectPicker } from 'rsuite';
import { useDebounceCallback } from 'usehooks-ts';
import getUserSession from 'features/auth/api/getUserSession';
import getDevSwitchableUsers, {
  DevSwitchableUser,
} from 'features/dev/api/getDevSwitchableUsers';
import serverErrorHandler from 'services/serverError.service';
import useUserStore from 'store/useUserStore';
import {
  clampDevSwitcherPosition,
  DEV_SWITCHER_VISIBLE_EVENT,
  DevSwitcherPosition,
  getDefaultDevSwitcherPosition,
  getDevImpersonateUserId,
  getDevSwitcherPosition,
  isDevSwitcherVisible,
  isDevUserSwitchEnabled,
  setDevImpersonateUserId,
  setDevSwitcherPosition,
  setDevSwitcherVisible,
} from 'utils/devUserSwitch';
import { getHomePath } from 'utils/navigation';
import styles from './DevUserSwitcher.module.scss';

const SELF_VALUE = '__self__';
const PANEL_WIDTH = 360;

export default function DevUserSwitcher() {
  const navigate = useNavigate();
  const sessionUser = useUserStore(s => s.user);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    origin: DevSwitcherPosition;
  } | null>(null);
  const [users, setUsers] = useState<DevSwitchableUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(isDevSwitcherVisible);
  const [impersonateId, setImpersonateId] = useState<string | null>(
    getDevImpersonateUserId,
  );
  const [position, setPosition] = useState<DevSwitcherPosition>(() => {
    const saved = getDevSwitcherPosition();
    return clampDevSwitcherPosition(
      saved?.x ?? getDefaultDevSwitcherPosition(PANEL_WIDTH).x,
      saved?.y ?? getDefaultDevSwitcherPosition(PANEL_WIDTH).y,
      PANEL_WIDTH,
    );
  });

  useEffect(() => {
    if (!isDevUserSwitchEnabled()) return undefined;
    const onVisible = (event: Event) => {
      const { detail } = event as CustomEvent<boolean>;
      setIsVisible(typeof detail === 'boolean' ? detail : isDevSwitcherVisible());
    };
    window.addEventListener(DEV_SWITCHER_VISIBLE_EVENT, onVisible);
    return () => {
      window.removeEventListener(DEV_SWITCHER_VISIBLE_EVENT, onVisible);
    };
  }, []);

  const pickerData = useMemo(
    () => [
      { label: 'My account (self)', value: SELF_VALUE },
      ...users.map(user => ({
        label: `${user.name} · ${user.email} (${user.type})`,
        value: user._id,
      })),
    ],
    [users],
  );

  const clampToViewport = useCallback((next: DevSwitcherPosition) => {
    const panelHeight = panelRef.current?.offsetHeight ?? 220;
    return clampDevSwitcherPosition(
      next.x,
      next.y,
      panelRef.current?.offsetWidth ?? PANEL_WIDTH,
      panelHeight,
    );
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPosition(current => {
        const clamped = clampToViewport(current);
        setDevSwitcherPosition(clamped);
        return clamped;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampToViewport]);

  const loadUsers = useCallback(async (search = '') => {
    setIsLoadingUsers(true);
    try {
      const payload = await getDevSwitchableUsers(search);
      setUsers(payload);
    } catch (error) {
      serverErrorHandler(error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const debouncedLoadUsers = useDebounceCallback(loadUsers, 300);

  useEffect(() => {
    if (!isDevUserSwitchEnabled() || !isVisible) return;
    debouncedLoadUsers(searchQuery);
  }, [searchQuery, debouncedLoadUsers, isVisible]);

  const applySession = useCallback(
    async (nextUserId: string | null) => {
      setIsSwitching(true);
      try {
        setDevImpersonateUserId(nextUserId);
        setImpersonateId(nextUserId);
        const response = await getUserSession();
        useUserStore.getState().setUser(response);
        navigate(getHomePath());
      } catch (error) {
        serverErrorHandler(error);
      } finally {
        setIsSwitching(false);
      }
    },
    [navigate],
  );

  const handleDragPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      origin: position,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStateRef.current) return;
    const { startX, startY, origin } = dragStateRef.current;
    const next = clampToViewport({
      x: origin.x + (event.clientX - startX),
      y: origin.y + (event.clientY - startY),
    });
    setPosition(next);
  };

  const handleDragPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setPosition(current => {
      const clamped = clampToViewport(current);
      setDevSwitcherPosition(clamped);
      return clamped;
    });
  };

  if (!isDevUserSwitchEnabled() || !sessionUser || !isVisible) {
    return null;
  }

  const activeUser = impersonateId
    ? users.find(user => user._id === impersonateId)
    : null;

  const hidePanel = () => {
    setDevSwitcherVisible(false);
    setIsVisible(false);
  };

  return (
    <div
      className={styles.overlay}
      data-testid="dev-user-switcher"
      style={{ left: position.x, top: position.y }}
    >
      <div
        ref={panelRef}
        className={styles.panel}
        data-dragging={isDragging ? 'true' : undefined}
      >
        <div className={styles.header}>
          <p className={styles.badge}>Dev only</p>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.dragHandle}
              aria-label="Move dev panel"
              onPointerDown={handleDragPointerDown}
              onPointerMove={handleDragPointerMove}
              onPointerUp={handleDragPointerUp}
              onPointerCancel={handleDragPointerUp}
            >
              ⋮⋮
            </button>
            <button
              type="button"
              className={styles.closeButton}
              aria-label="Hide dev panel"
              onClick={hidePanel}
            >
              ×
            </button>
          </div>
        </div>
        <p className={styles.label}>View portal as another My-TXT user</p>

        <Input
          className={styles.searchInput}
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search name or email"
          disabled={isSwitching || isDragging}
        />

        <SelectPicker
          className={styles.picker}
          popupClassName={styles.pickerPopup}
          data={pickerData}
          value={impersonateId ?? SELF_VALUE}
          onChange={value => {
            if (!value || value === SELF_VALUE) {
              applySession(null);
              return;
            }
            applySession(String(value));
          }}
          loading={isLoadingUsers || isSwitching}
          disabled={isSwitching || isDragging}
          searchable={false}
          cleanable={false}
          placeholder={searchQuery ? 'Select from search results' : 'Select user'}
          maxHeight={280}
        />

        <button
          type="button"
          className={styles.resetButton}
          disabled={!impersonateId || isSwitching}
          onClick={() => applySession(null)}
        >
          Reset to my account
        </button>

        <p className={styles.status}>
          {activeUser
            ? `Viewing as ${activeUser.name} · ${activeUser.type}`
            : 'Viewing as signed-in account'}
        </p>
      </div>
    </div>
  );
}
